import React, {useState, useEffect} from 'react';
import {useDispatch} from 'react-redux';
import moment from 'moment-timezone';
import {DatePicker} from '@mui/x-date-pickers/DatePicker';
import AppBar from '@mui/material/AppBar';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';

import OptionalSelect from '../../common/components/OptionalSelect';
import AutocompletePlace from './AutoCompletePlace';
import AutoCompleteTags from './AutoCompleteTags';
import {BluePillButton} from '../../common/components/BluePillButton';
import {createStudent, updateStudent} from '../../redux/slices/builder';

import baseStyles from '../../css/Base.module.css';

const GRADE_LEVELS = [
  'Kindergarten',
  'First grade',
  'Second grade',
  'Third grade',
  'Fourth grade',
  'Fifth grade',
  'Sixth grade',
  'Seventh grade',
  'Eighth grade',
  'Ninth grade',
  'Tenth grade',
  'Eleventh grade',
  'Twelfth grade',
];
const GENDERS = ['Male', 'Female', 'Prefer Not to Answer'];
const RACES = [
  'African American/Black',
  'American Indian or Alaska Native',
  'Asian',
  'Native Hawaiian or Other Pacific Islander',
  'White',
];
const ETHNICITIES = ['Hispanic or Latino/a', 'Not Hispanic or Latino/a'];
const LANGUAGES = [
  'Arabic',
  'Chinese',
  'English',
  'French',
  'German',
  'Haitian Creole',
  'Hindi',
  'Korean',
  'Portuguese',
  'Russian',
  'Spanish',
  'Tagalog',
  'Telugu',
  'Vietnamese',
];

export default function StudentForm({student, postSave, onCancel}) {
  const dispatch = useDispatch();

  const [mode, setMode] = useState(null); // CREATE, EDIT
  const [isSaving, toggleIsSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [school, setSchool] = useState(null);
  const [gradeLevel, setGradeLevel] = useState('');
  const [birthDate, setBirthDate] = useState(null);
  const [gender, setGender] = useState(null);
  const [races, setRaces] = useState([]);
  const [ethnicity, setEthnicity] = useState(null);
  const [language, setLanguage] = useState(null);
  const [tags, setTags] = useState([]);

  useEffect(() => {
    if (student) {
      setMode('EDIT');
    } else {
      setMode('CREATE');
    }
  }, [student]);

  useEffect(() => {
    if (student) {
      setFirstName(student.firstName);
      setLastName(student.lastName);
      setSchool(student.school);
      setGradeLevel(student.gradeLevel);
      setBirthDate(student.birthDate);
      if (student.gender) setGender(student.gender);
      setRaces(student.races);
      if (student.ethnicity) setEthnicity(student.ethnicity);
      if (student.language) setLanguage(student.language);
      setTags(student.tags);
    }
  }, [student]);

  function isSaveButtonEnabled() {
    return !!firstName && !!lastName && school && !!gradeLevel && !!birthDate && !isSaving;
  }

  async function handleClickSave() {
    toggleIsSaving(true);

    const tagIDs = tags.map((tag) => tag._id);
    const studentPayload = {
      firstName,
      lastName,
      school,
      gradeLevel,
      birthDate,
      gender: !!gender ? gender : null,
      races,
      ethnicity: !!ethnicity ? ethnicity : null,
      language: !!language ? language : null,
      tags: tagIDs,
    };
    handleSave(studentPayload);
  }

  async function handleSave(studentPayload) {
    let savedStudent;

    if (mode === 'CREATE') {
      savedStudent = await dispatch(
        createStudent(
          studentPayload.firstName,
          studentPayload.lastName,
          studentPayload.school,
          studentPayload.gradeLevel,
          studentPayload.birthDate,
          studentPayload.gender,
          studentPayload.races,
          studentPayload.ethnicity,
          studentPayload.language,
          studentPayload.tags,
        ),
      );
    } else if (mode === 'EDIT') {
      savedStudent = await dispatch(updateStudent(student.id, studentPayload));
    }

    toggleIsSaving(false);
    postSave(savedStudent);
  }

  function handleChangeRace(event) {
    const {
      target: {value},
    } = event;
    setRaces(typeof value === 'string' ? value.split(',') : value);
  }

  return (
    <>
      <div className="p-20">
        <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
          Student Details
        </Typography>
        <Typography variant="body2" className="fnt-bold">
          Required Information
        </Typography>
        <Grid container>
          <Grid item xs={8}>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <TextField
                  autoFocus
                  margin="dense"
                  label="First name"
                  onChange={(event) => setFirstName(event.target.value)}
                  value={firstName}
                  required
                  fullWidth
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  margin="dense"
                  label="Last name"
                  onChange={(event) => setLastName(event.target.value)}
                  value={lastName}
                  required
                  fullWidth
                />
              </Grid>
            </Grid>

            <Grid item xs={12} className="m-t-10">
              <AutocompletePlace
                onSelect={setSchool}
                initialValue={school}
                inputLabel="School"
                inputVariant="outlined"
                isRequired
              />
            </Grid>

            <Grid container spacing={2} className="m-t-10">
              <Grid item md={6} xs={12}>
                <FormControl fullWidth required>
                  <InputLabel>Grade level</InputLabel>
                  <Select
                    label="Grade level"
                    value={gradeLevel}
                    onChange={(event) => setGradeLevel(event.target.value)}
                  >
                    {GRADE_LEVELS.map((gradeLevel) => (
                      <MenuItem key={gradeLevel} value={gradeLevel}>
                        {gradeLevel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Birthdate *"
                    value={birthDate ? moment(birthDate) : null}
                    onChange={(momentDate) => setBirthDate(momentDate.format('YYYY-MM-DD'))}
                    disableFuture
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="body2" className="fnt-bold m-t-20 m-b-10">
              Optional Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth>
                  <OptionalSelect
                    label="Gender"
                    value={gender}
                    handleChange={(event, newValue) => setGender(newValue)}
                    options={GENDERS}
                  />
                </FormControl>
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Race</InputLabel>
                  <Select multiple value={races} onChange={handleChangeRace} input={<OutlinedInput label="Race" />}>
                    {RACES.map((race) => (
                      <MenuItem key={race} value={race}>
                        {race}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={2} className="m-t-10">
              <Grid item md={6} xs={12}>
                <FormControl fullWidth>
                  <OptionalSelect
                    label="Ethnicity"
                    value={ethnicity}
                    handleChange={(event, newValue) => setEthnicity(newValue)}
                    options={ETHNICITIES}
                  />
                </FormControl>
              </Grid>
              <Grid item md={6} xs={12}>
                <FormControl fullWidth>
                  <OptionalSelect
                    label="Home Language"
                    value={language}
                    handleChange={(event, newValue) => setLanguage(newValue)}
                    options={LANGUAGES}
                  />
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container className="m-t-30">
          <Grid item xs={12} md={6} xl={4}>
            <Typography variant="subtitle1" className="clr-dark-blue fnt-bold">
              Tags
            </Typography>
            <Typography variant="body2" className="fnt-bold">
              Create a tag system to organize and find students faster.
            </Typography>
            <AutoCompleteTags tags={tags} setTags={setTags} />
          </Grid>
        </Grid>
      </div>
      <AppBar position="fixed" className="bg-gray-100" sx={{top: 'auto', bottom: 0}}>
        <Toolbar>
          <Grid container direction="row" justifyContent="center" alignItems="center">
            <Button className={baseStyles['btn-pill']} onClick={onCancel}>
              Cancel
            </Button>
            <BluePillButton variant="outlined" disabled={!isSaveButtonEnabled()} onClick={handleClickSave}>
              {isSaving ? <CircularProgress size={25} /> : 'Save'}
            </BluePillButton>
          </Grid>
        </Toolbar>
      </AppBar>
      <Toolbar /> {/* Makes AppBar at the bottom of the page */}
    </>
  );
}
